import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Plus, Filter } from 'lucide-react';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import { reviewService } from '../services/reviewService';
import { useAuth } from '../contexts/AuthContext';
import { notifyApiError, notifySuccess } from "../utils/apiError";

const ReviewSection = ({ serviceType, serviceId, serviceName }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalReviews: 0,
    hasNext: false
  });
  const [sortBy, setSortBy] = useState('recent'); // recent, rating_high, rating_low
  const [filterRating, setFilterRating] = useState('all'); // all, 5, 4, 3, 2, 1
  
  const { currentUser } = useAuth();

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reviewService.getServiceReviews(
        serviceType, 
        serviceId, 
        {
          page: pagination.currentPage,
          limit: 10,
          sortBy,
          rating: filterRating !== 'all' ? filterRating : undefined
        }
      );
      
      if (response.success) {
        setReviews(response.data.reviews);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [serviceType, serviceId, pagination.currentPage, sortBy, filterRating]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleSubmitReview = async (reviewData) => {
    try {
      setSubmitting(true);
      const response = await reviewService.createReview(reviewData);
      
      if (response.success) {
        setShowForm(false);
        // Refresh reviews to show the new one (if approved immediately)
        await loadReviews();
        
        // Show success message
        notifySuccess('Votre avis a été soumis avec succès ! Il sera visible après modération.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      notifyApiError(error, "Erreur lors de la soumission de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoteHelpful = async (reviewId) => {
    try {
      await reviewService.voteHelpful(reviewId);
      // Refresh reviews to update vote count
      await loadReviews();
    } catch (error) {
      console.error('Error voting for review:', error);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, currentPage: newPage });
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.note, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      distribution[review.note]++;
    });
    return distribution;
  };

  const renderRatingFilter = () => {
    const distribution = getRatingDistribution();
    
    return (
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtrer par note:</span>
        </div>
        <select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Toutes les notes</option>
          <option value="5">5 étoiles ({distribution[5]})</option>
          <option value="4">4 étoiles ({distribution[4]})</option>
          <option value="3">3 étoiles ({distribution[3]})</option>
          <option value="2">2 étoiles ({distribution[2]})</option>
          <option value="1">1 étoile ({distribution[1]})</option>
        </select>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="recent">Plus récents</option>
          <option value="rating_high">Note décroissante</option>
          <option value="rating_low">Note croissante</option>
        </select>
      </div>
    );
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-center space-x-2 mt-8">
        <button
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          disabled={!pagination.hasPrev}
          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Précédent
        </button>
        
        <span className="px-3 py-2 text-sm text-gray-700">
          Page {pagination.currentPage} sur {pagination.totalPages}
        </span>
        
        <button
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          disabled={!pagination.hasNext}
          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Suivant
        </button>
      </div>
    );
  };

  return (
    <div className="mt-12">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <MessageSquare className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">
            Avis clients
          </h2>
          {pagination.totalReviews > 0 && (
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
              {pagination.totalReviews}
            </span>
          )}
        </div>
        
        {currentUser && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Laisser un avis</span>
          </button>
        )}
      </div>

      {/* Review Summary */}
      {reviews.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-3xl font-bold text-gray-900">
                  {getAverageRating()}
                </span>
                <div className="flex items-center">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span
                      key={i}
                      className={`text-lg ${
                        i < Math.round(getAverageRating())
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Basé sur {pagination.totalReviews} avis
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Review Form */}
      {showForm && (
        <div className="mb-8">
          <ReviewForm
            serviceType={serviceType}
            serviceId={serviceId}
            onSubmit={handleSubmitReview}
            onCancel={() => setShowForm(false)}
            isSubmitting={submitting}
          />
        </div>
      )}

      {/* Login prompt for non-authenticated users */}
      {!currentUser && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <p className="text-blue-800">
            <a href="/login" className="font-medium hover:underline">
              Connectez-vous
            </a>
            {' '}pour laisser un avis sur ce service.
          </p>
        </div>
      )}

      {/* Filters and Sorting */}
      {reviews.length > 0 && renderRatingFilter()}

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-6">
          {reviews.map((review) => (
            <ReviewCard
              key={review._id}
              review={review}
              onVoteHelpful={currentUser ? handleVoteHelpful : null}
            showVoteButton={!!currentUser}
            />
          ))}
          {renderPagination()}
        </div>
      ) : (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun avis pour le moment
          </h3>
          <p className="text-gray-600 mb-4">
            Soyez le premier à laisser un avis sur {serviceName}.
          </p>
          {currentUser && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Laisser le premier avis</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;